import { FileText, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import type { CustomNodeData, LoggerNodeConfig } from '../../types/nodes';
import { VariablePicker } from '../VariablePicker';

interface LoggerConfigProps {
  nodeId: string;
  data: CustomNodeData;
  onChange: (data: CustomNodeData) => void;
  // Wizard state callbacks for parent component
  onWizardStateChange?: (state: {
    wizardMode: boolean;
    currentStep: number;
    totalSteps: number;
    canProceedToNext: boolean;
    onPreviousStep: () => void;
    onNextStep: () => void;
    onCompleteWizard: () => Promise<void>;
    validationAttempted: boolean;
    validationFailed: boolean;
  }) => void;
  onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

const defaultConfig: LoggerNodeConfig = {
  logLevel: 'info'
};

const LOG_LEVELS = [
  { value: 'debug', label: 'Debug', color: 'gray', description: 'Detailed information for debugging' },
  { value: 'info', label: 'Info', color: 'blue', description: 'General information' },
  { value: 'warn', label: 'Warning', color: 'yellow', description: 'Warning messages' },
  { value: 'error', label: 'Error', color: 'red', description: 'Error messages' }
] as const;

export function LoggerConfig({ nodeId, data, onChange, onWizardStateChange, onValidationRequest }: LoggerConfigProps) {
  // Validation pattern implementation
  const {
    validationAttempted,
    validationFailed,
    performValidation,
    handleCompleteWizard,
  } = useNodeValidationPattern(nodeId, data);
  
  // Validation display state
  const { isValidating, lastValidationResult } = useNodeValidation();

  const { getAvailableFields, getFlattenedFields } = useWorkflowContext();
  const [config, setConfig] = useState<LoggerNodeConfig>(() => {
    const existingConfig = (data as any).config as Partial<LoggerNodeConfig>;
    return {
      ...defaultConfig,
      ...existingConfig,
    };
  });

  const availableFields = getAvailableFields(nodeId);
  const flattenedFields = getFlattenedFields(nodeId);

  const handleChange = (newConfig: Partial<LoggerNodeConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    onChange({
      ...data,
      config: updatedConfig,
    } as CustomNodeData);
  };

  const addField = () => {
    const currentFields = config.fields || [];
    handleChange({
      fields: [...currentFields, '']
    });
  };

  const removeField = (index: number) => {
    const currentFields = config.fields || [];
    const updated = [...currentFields];
    updated.splice(index, 1);
    handleChange({ fields: updated });
  };

  const updateField = (index: number, value: string) => {
    const currentFields = config.fields || [];
    const updated = [...currentFields];
    updated[index] = value;
    handleChange({ fields: updated });
  };

  const clearFields = () => {
    handleChange({ fields: undefined });
  };

  // Register validation function with parent
  useEffect(() => {
    if (onValidationRequest) {
      onValidationRequest(performValidation);
    }
  }, [onValidationRequest, performValidation]);

  // Wizard state management
  useEffect(() => {
    if (onWizardStateChange) {
      onWizardStateChange({
        wizardMode: false, // Logger config doesn't use wizard mode
        currentStep: 1,
        totalSteps: 1,
        canProceedToNext: true,
        onPreviousStep: () => {},
        onNextStep: () => {},
        onCompleteWizard: async () => {
          await handleCompleteWizard(() => {
            // Save the current configuration
            onChange({
              ...data,
              config,
            } as CustomNodeData);
          });
        },
        validationAttempted,
        validationFailed,
      });
    }
  }, [onWizardStateChange, config, validationAttempted, validationFailed, handleCompleteWizard, onChange, data]);

  return (
    <div className="flex flex-col h-full">
      {/* Validation Results */}
      {(isValidating || lastValidationResult) && (
        <div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
          <ValidationResultDisplay
            errors={lastValidationResult?.errors || []}
            isValidating={isValidating}
          />
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* Header */}
      <div>
        <h3 className="text-sm font-medium text-gray-900">Logger Configuration</h3>
        <p className="text-xs text-gray-500 mt-1">
          Debug and log specific fields or full context at runtime
        </p>
      </div>

      {/* Available Fields */}
      {availableFields.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Available Fields</h4>
          <div className="flex flex-wrap gap-1">
            {flattenedFields.slice(0, 15).map((field) => (
              <button
                key={field.id}
                onClick={() => {
                  const currentFields = config.fields || [];
                  if (!currentFields.includes(field.id)) {
                    handleChange({ fields: [...currentFields, field.id] });
                  }
                }}
                className={`px-2 py-1 text-xs border rounded transition-colors ${
                  (config.fields || []).includes(field.id)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'
                }`}
                title={`${field.description || field.label} - Click to add to log`}
              >
                {field.label}
              </button>
            ))}
            {flattenedFields.length > 15 && (
              <span className="text-xs text-blue-600">+{flattenedFields.length - 15} more</span>
            )}
          </div>
        </div>
      )}

      {/* Log Level */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">Log Level</label>
        <div className="grid grid-cols-2 gap-3">
          {LOG_LEVELS.map((level) => (
            <button
              key={level.value}
              onClick={() => handleChange({ logLevel: level.value })}
              className={`p-3 border rounded-lg text-left transition-colors ${
                config.logLevel === level.value
                  ? `border-${level.color}-500 bg-${level.color}-50 text-${level.color}-700`
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full bg-${level.color}-500`} />
                <div>
                  <div className="font-medium text-sm">{level.label}</div>
                  <div className="text-xs text-gray-500">{level.description}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Fields to Log */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700">Fields to Log</label>
          <div className="flex gap-2">
            <button
              onClick={clearFields}
              className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
            >
              Log All Context
            </button>
            <button
              onClick={addField}
              className="flex gap-1 items-center px-2 py-1 text-xs text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
            >
              <Plus className="w-3 h-3" />
              Add Field
            </button>
          </div>
        </div>

        {!config.fields ? (
          <div className="p-4 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
            <FileText className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium">Log Full Context</p>
            <p className="text-xs">All available data will be logged</p>
          </div>
        ) : (
          <div className="space-y-2">
            {config.fields.map((field, index) => (
              <div key={index} className="flex gap-2 items-center">
                <div className="flex-1">
                  <VariablePicker
                    nodeId={nodeId}
                    value={field}
                    onChange={(value) => updateField(index, value)}
                    placeholder="Field to log (e.g., {{user.email}})"
                    className="w-full text-sm"
                  />
                </div>
                <button
                  onClick={() => removeField(index)}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            {config.fields.length === 0 && (
              <div className="text-center py-4 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                <p className="text-xs">No specific fields configured</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configuration Preview */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Logger Behavior</h5>
        <div className="text-xs text-gray-600 space-y-1">
          <div><strong>Log Level:</strong> {config.logLevel?.toUpperCase() || 'INFO'}</div>
          <div><strong>Fields:</strong> {
            !config.fields ? 'Full context' : 
            config.fields.length === 0 ? 'None specified' :
            config.fields.length === 1 ? '1 field' :
            `${config.fields.length} fields`
          }</div>
          <div className="mt-2 text-gray-500">
            {!config.fields 
              ? 'All context data will be logged for debugging'
              : config.fields.length === 0
              ? 'No fields will be logged'
              : `Selected fields will be logged at ${config.logLevel} level`
            }
          </div>
        </div>
      </div>

      {/* Output Structure */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h5 className="text-sm font-medium text-blue-900 mb-2">Output Structure</h5>
        <div className="text-xs text-blue-800 space-y-1">
          <div>Logger node does not modify context data but provides logging:</div>
          <div className="ml-4 space-y-1 font-mono">
            <div>Console output at specified log level</div>
            <div>Timestamp and node information included</div>
            <div>Formatted field values for debugging</div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
} 